import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  offset,
  shift,
  size,
  useClick,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useListItem,
  useListNavigation,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { Fragment, memo, useRef, useState } from 'react';

import type { MenuElement, MenuProps } from './types';

import { Action } from '../../types/Action';
import { useEscapePriorityDismiss } from '../../utils/floatingDismissPriority';
import { resolveTopLayerRoot } from '../../utils/resolveTopLayerRoot';
import { BreadcrumbLink } from '../Breadcrumb/types';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import './Menu.css';
import { MenuButtonItem, MenuLinkItem } from './subcomponents/MenuItem';

const VIEWPORT_PADDING = 8;

export const Menu = memo((props: MenuProps) => {
  const {
    as,
    direction = 'bottom-start',
    header,
    label,
    menuItems = [],
    onOpenChange,
    returnFocus = true,
    trigger,
    __private,
  } = props;
  const groupedMenuItems =
    props.as === 'button' ? props.groupedMenuItems : undefined;

  const [isOpen, setIsOpenState] = useState(__private?.defaultOpen ?? false);
  const setIsOpen = (open: boolean) => {
    setIsOpenState(open);
    onOpenChange?.(open);
  };
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  /* `useListNavigation` syncs focus to the hovered row so the arrow keys
     continue from wherever the pointer is. That means an ungated
     `:focus-visible` drags the keyboard ring around with the mouse, since the
     browser keeps matching it once a keyboard interaction has happened. The
     ring is painted only while this is set. */
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  // Resolved as the menu opens: the trigger is mounted by then, and its host can
  // change between openings (a dialog closing, a popover reused).
  const [portalRoot, setPortalRoot] = useState<HTMLElement | undefined>();

  const triggerRef = useRef<HTMLElement | null>(null);
  const elementsRef = useRef<Array<MenuElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);

  const nodeId = useFloatingNodeId();
  const item = useListItem();

  const { floatingStyles, refs, context } = useFloating<HTMLElement>({
    middleware: [
      offset({ mainAxis: 4, alignmentAxis: 0 }),
      flip(),
      shift(),
      size({
        padding: VIEWPORT_PADDING,
        apply({ availableHeight, elements }) {
          /*
           * The system has no token for a dropdown's maximum height, so the
           * cap comes from the space the viewport actually offers rather than
           * a hardcoded value.
           */
          elements.floating.style.setProperty(
            '--_available-height',
            `${availableHeight}px`,
          );
        },
      }),
    ],
    nodeId,
    open: isOpen,
    placement: direction,
    // A top-layer host clips the portalled menu when it hides its own overflow;
    // fixed positioning escapes that without leaving the layer.
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    onOpenChange: (open) => {
      if (open) {
        setPortalRoot(resolveTopLayerRoot(triggerRef.current));
      }
      setIsOpen(open);
    },
  });

  const click = useClick(context, {
    event: 'mousedown',
    toggle: true,
  });
  const role = useRole(context, { role: 'menu' });
  const dismiss = useEscapePriorityDismiss(context);
  const listNavigation = useListNavigation(context, {
    activeIndex,
    disabledIndices: (index) => !elementsRef.current[index],
    listRef: elementsRef,
    onNavigate: setActiveIndex,
  });

  const {
    getReferenceProps: getRawReferenceProps,
    getFloatingProps,
    getItemProps,
  } = useInteractions([click, role, dismiss, listNavigation]);

  /* `useRole` marks the trigger `role="menuitem"` whenever *any* ancestor
     `FloatingNode` exists, which it takes as "I must be a submenu" — but
     Modal/Popover/Select now join the same `FloatingTree` too, purely for
     Escape-key priority, so a plain trigger inside one of those is wrongly
     seen as nested. `item.index` (from `useListItem` above) is the precise
     signal for genuine nesting: it's only set when this trigger actually
     registered inside an ancestor Menu's own `FloatingList`, i.e. a real
     submenu. Drop the injected role everywhere else. */
  const getReferenceProps: typeof getRawReferenceProps = (userProps) => {
    const rawReferenceProps = getRawReferenceProps(userProps);
    if (item.index !== -1) return rawReferenceProps;
    const { role: _role, ...referenceProps } = rawReferenceProps;
    return referenceProps;
  };

  const ref = useMergeRefs([refs.setReference, item.ref, triggerRef]);

  const floatingProps = getFloatingProps({
    onKeyDown: () => setIsKeyboardNav(true),
    onPointerMove: () => setIsKeyboardNav(false),
  });

  /* `useRole` names the open menu with `aria-labelledby` pointing at the
     trigger, and `aria-labelledby` outranks `aria-label`, so an explicit name
     only takes effect once the inherited one is cleared. */
  const menuNameProps: Record<string, string | undefined> = label
    ? { 'aria-label': label, 'aria-labelledby': undefined }
    : {};

  const renderMenuItem = (item: Action | BreadcrumbLink, key: string) => {
    if (as === 'link') {
      const link = item as BreadcrumbLink;
      return <MenuLinkItem {...link} key={key} __private={{ getItemProps }} />;
    }

    const action = item as Action;

    return (
      <MenuButtonItem
        {...action}
        key={key}
        onClick={() => {
          try {
            action.onClick?.();
          } finally {
            setIsOpen(false);
          }
        }}
        __private={{ getItemProps }}
      />
    );
  };

  return (
    <>
      {trigger({ isOpen, ref, getReferenceProps })}
      <FloatingList elementsRef={elementsRef} labelsRef={labelsRef}>
        {isOpen && (
          <FloatingPortal root={portalRoot}>
            <FloatingFocusManager
              context={context}
              initialFocus={0}
              modal={false}
              returnFocus={returnFocus}
            >
              <div
                className="eds-menu"
                data-keyboard-nav={isKeyboardNav || undefined}
                ref={refs.setFloating}
                style={floatingStyles}
                {...floatingProps}
                {...menuNameProps}
              >
                {header && (
                  <>
                    <div className="eds-menu--header">{header}</div>
                    <div className="eds-menu--group-divider" />
                  </>
                )}
                {groupedMenuItems?.length ? (
                  groupedMenuItems.map((group, index) => (
                    <Fragment key={index}>
                      {Boolean(index > 0) && (
                        <div className="eds-menu--group-divider" />
                      )}
                      <div
                        aria-label={group.title || undefined}
                        role={group.title ? 'group' : undefined}
                      >
                        {group.title && (
                          <Layout className="eds-menu--group-header">
                            <Text as="p" tone="subdued" variant="overline">
                              {group.title}
                            </Text>
                          </Layout>
                        )}
                        <Layout
                          className="eds-menu--items"
                          direction="column"
                          spacing="none"
                        >
                          {group.items.map((action) =>
                            renderMenuItem(action, action.label),
                          )}
                        </Layout>
                      </div>
                    </Fragment>
                  ))
                ) : (
                  <Layout
                    className="eds-menu--items"
                    direction="column"
                    spacing="none"
                  >
                    {menuItems.map((menuItem) =>
                      renderMenuItem(
                        menuItem,
                        as === 'link'
                          ? ((menuItem as BreadcrumbLink).text ?? '')
                          : (menuItem as Action).label,
                      ),
                    )}
                  </Layout>
                )}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingList>
    </>
  );
});

Menu.displayName = 'Menu';
